import { Address } from './Address';

export interface Unit {
  id: string;
  name: string;
  beds: number;
  baths: number;
  unitSizeSqft: number;
  propertyId: string;
  portfolioId: string;
  isActive: boolean;
  isAvailable: boolean;
  images: UnitImage[];
  address: Address;
}

export interface CompanyUnit {
  availableFrom?: Date;
  baths?: number;
  beds?: number;
  id?: string;
  leaseTerm?: LeaseTerm;
  listed?: Date;
  pinned?: boolean;
  primaryImage?: EmbedFileDto;
  primaryVideo?: EmbedFileDto;
  rentalPrice?: number;
  singleLineAddress?: string;
  title?: string;
  unitSizeSqft?: number;
  marketRent?: number;
}

/**
 * EmbedFileDto
 */
export interface EmbedFileDto {
  default?: boolean;
  description?: string;
  fileId?: string;
  signedThumbUrl?: string;
  signedUrl?: string;
}

export interface UnitImage {
  fileId: string;
  fileName?: string;
  signedUrl?: string;
  signedThumbUrl?: string;
  fileSize?: number;
  default: boolean;
  description?: string;
}

export enum PropertyType {
  SINGLE_FAMILY = 'singleFamily',
  MULTI_FAMILY = 'multiFamily',
  COMMERCIAL = 'commercial',
  CONDO = 'condo',
  TOWNHOME = 'townHome',
  OTHER = 'other',
}

export interface UnitParams {
  availableFrom?: string;
  maxRentalPrice?: number;
  minBaths?: number;
  minBeds?: number;
  minRentalPrice?: number;
  page?: number;
  pageSize?: number;
  pinned?: string;
  propertyTypes?: PropertyType[];
  search?: string;
}

export interface PaginatedUnits<T = CompanyUnit> {
  currentPage: number;
  items: T[];
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface PresentableUnit {
  id: string;
  name: string;
  area: string;
  bedrooms: string;
  bathrooms: string;
  image?: string;
  price: string;
  isLiked: boolean;
  pinned?: boolean;
  address?: string;
  marketRent?: number;
}

export interface PresentPaginUnitListing {
  currentPage: number;
  items: PresentableUnit[];
  allItems: PresentableUnit[];
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface PresentUnitParams {
  searchParams: UnitParams;
  priceRangeOptions: { label: string; value: string }[];
  minPriceOptions: { label: string; value: string }[];
  maxPriceOptions: { label: string; value: string }[];
  bedOptions?: { label: string; value: string }[];
  bathOptions?: { label: string; value: string }[];
  homeTypeOptions: { label: string; value: string }[];
  priceDisplay?: string;
  bdBaDisplay?: string;
  propertyTypesDisplay?: string;
}

/**
 * TenantHydratedUnitListingDto
 */
export interface TenantHydratedUnitListingDto {
  amenities?: AmenityDto[];
  availableFrom?: Date;
  baths?: number;
  beds?: number;
  description?: string;
  descriptionTranslations?: TranslationDto[];
  id?: string;
  images?: EmbedFileDto[];
  leaseTerm?: LeaseTerm;
  listed?: Date;
  locationAnalysis?: string;
  petPolicy?: PetPolicy;
  pinned?: boolean;
  primaryImage?: EmbedFileDto;
  primaryVideo?: EmbedFileDto;
  rentalPrice?: number;
  securityDeposit?: number;
  singleLineAddress?: string;
  tags?: string[];
  terms?: { [key: string]: string };
  title?: string;
  unit?: UnitDto;
  unitSizeSqft?: number;
  updated?: Date;
  videos?: EmbedFileDto[];
}

/**
 * AmenityDto
 */
export interface AmenityDto {
  avalability?: AmenityAvalability;
  description?: string;
  icon?: string;
  name?: string;
  type?: string;
}

/**
 * AmenityAvalability
 */
export enum AmenityAvalability {
  Available = 'available',
  Limited = 'limited',
  Pending = 'pending',
  Unavailable = 'unavailable',
}

/**
 * TranslationDto
 */
export interface TranslationDto {
  language?: string;
  value?: string;
}

/**
 * EmbedFileDto
 */
export interface EmbedFileDto {
  default?: boolean;
  description?: string;
  fileId?: string;
  signedThumbUrl?: string;
  signedUrl?: string;
}

/**
 * LeaseTerm
 */
export enum LeaseTerm {
  HalfYear = 'halfYear',
  MonthToMonth = 'monthToMonth',
  OneYear = 'oneYear',
  Other = 'other',
  ThreeYears = 'threeYears',
  TwoYears = 'twoYears',
}

export interface AllowedPetTypes {
  cat?: boolean;
  dog?: boolean;
  fish?: boolean;
  bird?: boolean;
  reptile?: boolean;
  other?: boolean;
}

/**
 * PetPolicy
 */
export interface PetPolicy {
  allowed?: boolean;
  allowedPetTypes?: AllowedPetTypes;
  size?: 'small' | 'medium' | 'large';
  fee?: number;
  deposit?: number;
  description?: string;
}

/**
 * UnitDto
 */
export interface UnitDto {
  active: boolean;
  available: boolean;
  /**
   * Value must be a double greater than or equal to 0 and evenly divisible by 0.5.
   */
  baths?: number;
  /**
   * Value must be a double greater than or equal to 0 and evenly divisible by 0.5.
   */
  beds?: number;
  icons: string[];
  id: string;
  images: UnitImageDto[];
  name: string;
  portfolioId: string;
  propertyId: string;
  /**
   * Value must be either null or greater than or equal to 0.
   */
  rentalPrice?: number;
  /**
   * Value must be either null or greater than or equal to 0.
   */
  unitSizeSqft?: number;
}

/**
 * UnitImageDto
 */
export interface UnitImageDto {
  default: boolean;
  description?: string;
  fileId: string;
}

/**
 * TenantUnitDescriptionTranslationDto
 */
export interface TenantUnitDescriptionTranslationDto {
  description?: string;
  language: string;
  status: TranslationStatus;
  title?: string;
}

/**
 * TranslationStatus
 */
export enum TranslationStatus {
  Completed = 'completed',
  Failed = 'failed',
  Pending = 'pending',
}

/**
 * TenantUnitListingImageDto
 */
export interface TenantUnitListingImageDto {
  default: boolean;
  description?: string;
  fileId: string;
  fileName: string;
  signedThumbUrl?: string;
  signedUrl: string;
}

/**
 * TenantPortfolioDto
 */
export interface TenantPortfolioDto {
  id: string;
  name: string;
}

/**
 * TenantPropertyDto
 */
export interface TenantPropertyDto {
  address?: AddressDto;
  amenities: { [key: string]: boolean };
  description?: string;
  descriptionsTranslations: TenantPropertyDescriptionTranslationDto[];
  displayName: string;
  id: string;
  name?: string;
  petPolicy?: PetPolicy;
  portfolioId: string;
  type: PropertyType;
  unitCount: number;
}

/**
 * AddressDto
 */
export interface AddressDto {
  city?: string;
  country?: string;
  state?: string;
  streetAddress1?: string;
  streetAddress2?: string;
  zipCode?: string;
}

/**
 * TenantPropertyDescriptionTranslationDto
 */
export interface TenantPropertyDescriptionTranslationDto {
  description: string;
  language: string;
}

/**
 * PropertyType
 */
export enum PropertyType {
  Commercial = 'commercial',
  Condo = 'condo',
  MultiFamily = 'multiFamily',
  Other = 'other',
  SingleFamily = 'singleFamily',
  TownHome = 'townHome',
}

/**
 * TenantUnitDto
 */
export interface TenantUnitDto {
  active: boolean;
  available: boolean;
  /**
   * Value must be a double greater than or equal to 0 and evenly divisible by 0.5.
   */
  baths?: number;
  /**
   * Value must be a double greater than or equal to 0 and evenly divisible by 0.5.
   */
  beds?: number;
  icons: string[];
  id: string;
  name: string;
  portfolioId: string;
  propertyId: string;
  /**
   * Value must be either null or greater than or equal to 0.
   */
  rentalPrice?: number;
  /**
   * Value must be either null or greater than or equal to 0.
   */
  unitSizeSqft?: number;
}

export interface Feature {
  name: string;
  children?: string[];
}

export interface PresentCompanyUnitListingDetail {
  id: string;
  name: string;
  area: string;
  bedrooms: string;
  bathrooms: string;
  address: string;
  displayAddress: string;
  images: { url: string; title: string }[];
  isSelected?: boolean;
  isAvailable?: boolean;
  price: string;
  description: string;
  petPolicy?: PetPolicy & { pets?: string };
  listed?: string;
  amenities?: { name: string }[];
  features?: Feature[];
  hasFeatures?: boolean;
  descriptionTranslations?: TranslationDto[];
}
